import React from 'react';
import type { ComponentType } from 'react';
import { cn } from '../commons/utils';
import type { IconLike, IconProps } from './icon.types';

export type IconSkin =
  | 'brand'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'warning'
  | 'success'
  | 'info';

const skinMap: Record<IconSkin, string> = {
  brand: 'var(--color-text-brand-primary)',
  primary: 'var(--color-text-neutral-primary)',
  secondary: 'var(--color-text-secondary)',
  tertiary: 'var(--color-text-tertiary)',
  destructive: 'var(--color-text-destructive-primary)',
  warning: 'var(--color-text-warning-primary)',
  success: 'var(--color-text-success-primary)',
  info: 'var(--color-bg-inform-primary)',
};

/** @__NO_SIDE_EFFECTS__ */
export const createIcon = (
  IconSvg: ComponentType<{
    viewBox: string;
    className?: string;
    skin?: IconSkin;
  }>,
  displayName: string,
  svgSize: number | [number, number] = 24
): IconLike => {
  const [w, h] = Array.isArray(svgSize) ? svgSize : [svgSize, svgSize];
  const viewBox = `0 0 ${w} ${h}`;
  const aspectRatio = w / h;

  const component: IconLike = ({
    size = 16,
    testId,
    className,
    skin,
    ...rest
  }: IconProps) => (
    <IconSvg
      data-testid={testId}
      viewBox={viewBox}
      className={cn('shrink-0 text-current', className)}
      style={{
        ...(aspectRatio === 1
          ? { width: size, height: size }
          : { height: size, width: Math.round(size * aspectRatio) }),
        ...(skin ? { color: skinMap[skin] } : {}),
      }}
      {...rest}
    />
  );

  component.displayName = displayName;

  return component;
};
